//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "@schema";
// libraries
import * as React from "react";
import { connect } from "react-redux";
import { createRefetchContainer } from "react-relay";
// chrome-autopilot-list imports
import { IProps, IContainer, IState, AutopilotListMode } from "./AutopilotList.d";
import { styles } from "./AutopilotList.styles";
// other components
import { Meeting, TMeetingAction } from "./Meeting";
import { H1, H2, P } from "@ui";
import { CalloutBox } from "@components/callout-box";
import { asCard } from "@components/card";
// state
import { IReduxState } from "@reducers/root.reducer";
import { setActiveViewEvent } from "@actions/chrome-extension.actions";
// util
import { logger } from "@util/logger.util";
import { track, page, TrackingEvents, PageEvents } from "@util/analytics.util";
import { calendarIdsToPersonMap } from "@util/calendar-id.util";
import { isOnTriggerDefrag } from "@util/experiment.util";
import { snackBarOpen } from "@actions/snack-bar.actions";
import { triggerDefragRun } from "@mutations";
import { TranslationContext } from "@clockwise/client-commons/src/providers/TranslationContext";
import { translate } from "@clockwise/client-commons/src/util/translations";
// material-ui
import withStyles from "@material-ui/core/styles/withStyles";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
// styles
import { greys } from "@clockwise/web-commons/src/styles/color.styles";

//////////////////
// COMPONENT
//////////////////
class AutopilotListCmpt extends React.Component<IProps, IState> {
  public static defaultProps = {
    mode: AutopilotListMode.Card,
    showHistory: true,
  };

  // ~-~-~-~-~-~-~-
  // Setup
  // ~-~-~-~-~-~-~-
  constructor(props: IProps) {
    super(props);

    const autopilotEventsResponse =
      (props.org &&
        props.org.autopilotEventsResponse &&
        props.org.autopilotEventsResponse.__typename !== "GraphEntityError" &&
        (props.org.autopilotEventsResponse as ISchema.IAutopilotEventsResponse)) ||
      null;

    const suggestedAutopilot =
      (autopilotEventsResponse && autopilotEventsResponse.suggestedOrganized) || [];

    this.state = {
      autopilotEventsResponse,
      suggestedAutopilot,
      personMap: {},
      suggestedInvite: [],
      dialogOpen: false,
    };
  }

  static contextType = TranslationContext;
  declare context: React.ContextType<typeof TranslationContext>;

  // ~-~-~-~-~-~-~-
  // Helper
  // ~-~-~-~-~-~-~-
  private onClickMeeting = (meeting: ISchema.IEvent) => {
    track(TrackingEvents.AUTOPILOT_LIST.EVENT_CLICKED, {
      externalEventId: meeting.eventKey.externalEventId,
    });

    const externalEventId = meeting.eventKey.externalEventId;
    const calendarId = meeting.organizer.urnValue;

    logger.debug("Setting ActiveViewEvent from AutopilotList", { externalEventId, calendarId });

    if (this.props.onClick) {
      this.props.onClick(externalEventId, calendarId);
    } else {
      this.props.setActiveViewEvent(externalEventId, calendarId, false);
    }
  };

  private sortEventsByDateAsc = (a: ISchema.IEvent, b: ISchema.IEvent) =>
    a.startTime.millis - b.startTime.millis;

  private onRemoveCard = () => {
    // nop
  };

  private onTriggerDefragRun = () => {
    triggerDefragRun(
      this.props.relay.environment,
      { orgRelayId: this.props.org.id },
      () => this.completeTriggerDefragRun(true),
      () => this.completeTriggerDefragRun(false),
    );
  };

  private completeTriggerDefragRun(result: boolean) {
    if (result) {
      this.props.snackBarOpen("An Autopilot run will begin shortly...");
    } else {
      this.props.snackBarOpen("Sorry, there was a problem triggering Autopilot to run.");
    }
  }

  // ~-~-~-~-~-~-~-
  // Life-Cycle
  // ~-~-~-~-~-~-~-
  public componentDidMount() {
    page(PageEvents.AUTOPILOT_LIST);

    const { org } = this.props;
    const { autopilotEventsResponse, suggestedAutopilot, suggestedInvite } = this.state;

    // get calendarIds
    let calendarIds: string[] = [];
    if (autopilotEventsResponse) {
      calendarIds = autopilotEventsResponse.suggestedAttending.map((sa) => sa.organizer.urnValue);
    }

    // get externalEventIds
    let externalEventIds: string[] = [];
    if (autopilotEventsResponse) {
      externalEventIds = autopilotEventsResponse.suggestedAttending.map(
        (sa) => sa.eventKey.externalEventId,
      );
    }

    /// refetch
    if (calendarIds.length) {
      this.props.relay.refetch({
        calendarIds,
        externalEventIds,
        orgRelayId: org.id,
      });
    }

    if (this.props.onLoad && autopilotEventsResponse) {
      const firstExternalEvent = autopilotEventsResponse.enabledOrganized.length
        ? autopilotEventsResponse.enabledOrganized[0]
        : autopilotEventsResponse.enabledAttending.length
        ? autopilotEventsResponse.enabledAttending[0]
        : suggestedAutopilot.length
        ? suggestedAutopilot[0]
        : suggestedInvite.length
        ? suggestedInvite[0]
        : undefined;

      this.props.onLoad(firstExternalEvent);
    }
  }

  public UNSAFE_componentWillReceiveProps(nextProps: IProps) {
    if (
      nextProps.orgRefetch.personsByCalendarIdsErrorable.__typename !== "GraphEntityError" &&
      nextProps.orgRefetch.personsByCalendarIdsErrorable.list.length &&
      !this.state.personMap.length
    ) {
      // generate the person map
      const personMap = calendarIdsToPersonMap(nextProps.orgRefetch);

      // generate can autopilot map
      const eventAutopilotMap: { [id: string]: boolean } = {};
      if (
        nextProps.orgRefetch.canScheduleResponse.__typename !== "GraphEntityError" &&
        nextProps.orgRefetch.canScheduleResponse.list.length
      ) {
        const canScheduleResponse = nextProps.orgRefetch
          .canScheduleResponse as ISchema.ICanScheduleResponseList;
        canScheduleResponse.list.forEach((csr) => {
          eventAutopilotMap[csr.externalEventId] = csr.canAutopilot;
        });
      }

      // iterate through suggested attending and split between autopilot or invite
      const { autopilotEventsResponse } = this.state;
      const newSuggestedAutopilot: ISchema.IEvent[] = [];
      const newSuggestedInvite: ISchema.IEvent[] = [];
      if (autopilotEventsResponse) {
        newSuggestedAutopilot.push(...autopilotEventsResponse.suggestedOrganized);
        autopilotEventsResponse.suggestedAttending.forEach((sa) => {
          const organizerPerson = personMap[sa.organizer.urnValue];
          const canAutopilot = eventAutopilotMap[sa.eventKey.externalEventId];
          if (canAutopilot) {
            newSuggestedAutopilot.push(sa);
          } else if (organizerPerson) {
            newSuggestedInvite.push(sa);
          }
        });
      }

      this.setState({
        personMap,
        suggestedAutopilot: newSuggestedAutopilot,
        suggestedInvite: newSuggestedInvite,
      });
    }
  }

  // ~-~-~-~-~-~-~-
  // Rendering
  // ~-~-~-~-~-~-~-
  private renderMeeting = (meeting: ISchema.IEvent, index: number, action?: TMeetingAction) => {
    const { org, relay, mode, calendarId } = this.props;

    return (
      <React.Fragment key={meeting.eventKey.externalEventId}>
        {index === 0 && mode === AutopilotListMode.Menu ? <hr style={styles.divider} /> : null}
        <Meeting
          mode={mode}
          isSelected={meeting.eventKey.externalEventId === this.props.selectedExternalEventId}
          org={org}
          meeting={meeting}
          environment={relay.environment}
          personListErrorable={this.props.orgRefetch.personsByCalendarIdsErrorable}
          personMap={this.state.personMap}
          onClickMeeting={this.onClickMeeting}
          removeCard={this.onRemoveCard}
          action={action}
          calendarId={calendarId}
        />
        {mode === AutopilotListMode.Menu ? <hr style={styles.divider} /> : null}
      </React.Fragment>
    );
  };

  private renderMeetingWithNoAction = (meeting: ISchema.IEvent, index: number) => {
    return this.renderMeeting(meeting, index);
  };

  private renderMeetingWithAutopilotAction = (meeting: ISchema.IEvent, index: number) => {
    return this.renderMeeting(meeting, index, "autopilot");
  };

  private renderMeetingWithInviteAction = (meeting: ISchema.IEvent, index: number) => {
    return this.renderMeeting(meeting, index, "invite");
  };

  private renderAutopilotList = () => {
    const t = this.context;

    // check for data requirements
    const { autopilotEventsResponse, suggestedAutopilot, suggestedInvite } = this.state;
    const { org } = this.props;

    const calloutStyle =
      this.props.mode === AutopilotListMode.Menu ? { margin: "20px 10px" } : { marginTop: 20 };

    // catch errors in fetches
    if (!autopilotEventsResponse) {
      return (
        <div className={this.props.classes.content}>
          <CalloutBox warning style={calloutStyle}>
            Sorry, loading your meetings on Autopilot failed. Please return home using the back
            arrow above and try again.
          </CalloutBox>
        </div>
      );
    }

    const h2Margin = this.props.mode === AutopilotListMode.Menu ? { marginLeft: 20 } : {};

    const hasEnabledEvents = !!(
      autopilotEventsResponse.enabledOrganized.length ||
      autopilotEventsResponse.enabledAttending.length
    );
    const hasSuggestedEvents = !!suggestedAutopilot.length;

    const h2Enabled = this.props.showHistory ? { padidngTop: 40 } : {};
    const h2SuggestedAP = this.props.showHistory || hasEnabledEvents ? { paddingTop: 40 } : {};
    const h2SuggestedInvite =
      this.props.showHistory || hasEnabledEvents || hasSuggestedEvents ? { paddingTop: 40 } : {};

    const hasEvents = hasEnabledEvents || hasSuggestedEvents || !!suggestedInvite.length;
    const showTriggerDefrag = isOnTriggerDefrag();

    return (
      <div className={this.props.classes.content}>
        {this.props.showHistory ? (
          <div cw-id="focus-time-card">
            <H2 style={{ ...styles.h2, ...h2Margin, marginBottom: 0 }}>
              {t("autopilot:history-title")}
            </H2>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div style={{ width: "100%" }} cw-id="focus-time-created">
                <FocusTimeSavedCard
                  hours={org.focusTimeSavedInHours}
                  message={"of Focus Time created for your company"}
                />
              </div>
              {/* <div style={{ marginLeft: 5 }}>
             <FocusTimeSavedCard hours={org.focusTimeSavedInHours} message={'Total Focus Time for coworkers'} />
           </div> */}
            </div>
          </div>
        ) : null}
        {!hasEvents ? (
          <CalloutBox info style={calloutStyle}>
            Suggested meetings will show up here once we identify any.
          </CalloutBox>
        ) : null}
        {hasEnabledEvents ? (
          <div cw-id={"your-flexible-meetings"}>
            <H2 style={{ ...styles.h2, ...h2Margin, ...h2Enabled }}>
              {t("autopilot:meetings-title")}
            </H2>
            {autopilotEventsResponse.enabledOrganized
              .concat(autopilotEventsResponse.enabledAttending)
              .sort(this.sortEventsByDateAsc)
              .map(this.renderMeetingWithNoAction)}
          </div>
        ) : (
          ""
        )}
        {suggestedAutopilot.length ? (
          <div>
            <H2 style={{ ...styles.h2, ...h2Margin, ...h2SuggestedAP }}>
              {t("autopilot:suggestions-title")}
            </H2>
            {suggestedAutopilot
              .slice()
              .sort(this.sortEventsByDateAsc)
              .map(this.renderMeetingWithAutopilotAction)}
          </div>
        ) : (
          ""
        )}
        {suggestedInvite.length ? (
          <div>
            <H2 style={{ ...styles.h2, ...h2Margin, ...h2SuggestedInvite }}>
              Invite the meeting organizer
            </H2>
            {suggestedInvite
              .slice()
              .sort(this.sortEventsByDateAsc)
              .map(this.renderMeetingWithInviteAction)}
          </div>
        ) : (
          ""
        )}
        {showTriggerDefrag ? (
          <div>
            <H2 style={{ ...styles.h2, ...h2Margin, ...h2SuggestedInvite }}>Run Autopilot now</H2>
            <Button
              variant="contained"
              onClick={() => this.setState({ dialogOpen: true })}
              color="primary"
            >
              Trigger an Autopilot run
            </Button>
            <Dialog open={this.state.dialogOpen} PaperProps={{ style: { width: 450 } }}>
              <DialogTitle>
                <H1 style={{ fontSize: 22, marginBottom: 0, paddingBottom: 0 }}>
                  Trigger an Autopilot run?
                </H1>
              </DialogTitle>
              <DialogContent style={{ paddingTop: 0, paddingBottom: 0 }}>
                <P
                  style={{
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: greys.slate,
                    marginBottom: 30,
                  }}
                >
                  Are you sure?
                </P>
              </DialogContent>
              <DialogActions>
                <Button variant="text" onClick={() => this.setState({ dialogOpen: false })}>
                  Cancel
                </Button>
                <Button
                  variant="text"
                  onClick={() => {
                    this.onTriggerDefragRun();
                    this.setState({ dialogOpen: false });
                  }}
                >
                  Do it
                </Button>
              </DialogActions>
            </Dialog>
          </div>
        ) : null}
      </div>
    );
  };

  public render() {
    return (
      <TranslationContext.Provider value={translate()}>
        {this.renderAutopilotList()}
      </TranslationContext.Provider>
    );
  }
}

//////////////////
// STYLES
//////////////////
const AutopilotListStyled = withStyles(styles)(AutopilotListCmpt);

//////////////////
// REDUX
//////////////////

function mapStateToProps(_state: IReduxState, _ownProps: IContainer) {
  return {};
}

const AutopilotListRedux = connect(mapStateToProps, {
  setActiveViewEvent,
  snackBarOpen,
})(AutopilotListStyled);

//////////////////
// RELAY
//////////////////
import { fragments, refetchQuery } from "./AutopilotList.gql";
export const AutopilotList = createRefetchContainer(AutopilotListRedux, fragments, refetchQuery);

interface IFocusTimeSavedProps {
  hours: number;
  message: string;
  removeCard?: () => void;
}

class FocusTimeSavedCmpt extends React.PureComponent<IFocusTimeSavedProps, {}> {
  public render() {
    return (
      <div style={{ padding: 14 }}>
        <H2 style={{ display: "flex", alignItems: "center", marginBottom: 0 }}>
          <span style={{ fontSize: 35 }}>+{this.props.hours}</span>
          <span style={{ marginLeft: 5, paddingTop: 5 }}>hrs</span>
        </H2>
        <span style={{ fontSize: 14 }}>{this.props.message}</span>
      </div>
    );
  }
}

const FocusTimeSavedCard = asCard<IFocusTimeSavedProps>(FocusTimeSavedCmpt);
